/**
 * Builds the contents list on policy pages from the headings already in the
 * document, and marks the section you are currently reading.
 *
 * Deriving it from the body rather than maintaining a second list means editing
 * a policy in the Shopify admin updates its navigation automatically — the two
 * cannot drift apart.
 *
 * Entirely additive: the nav starts hidden in the markup and is only revealed
 * once there is something to put in it, so with JavaScript off the page is a
 * clean document rather than an empty widget.
 */

(function () {
  "use strict";

  var body = document.querySelector("[data-policy-body]");
  var nav = document.querySelector("[data-policy-toc]");
  var list = document.querySelector("[data-policy-toc-list]");
  if (!body || !nav || !list) return;

  var headings = [].slice.call(body.querySelectorAll("h2, h3"));
  /* One heading is a document, not a structure worth navigating. */
  if (headings.length < 3) return;

  var used = Object.create(null);

  function slug(text) {
    var base =
      text
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-|-$/g, "")
        .slice(0, 60) || "section";

    /* Two clauses can legitimately share a title, and duplicate ids would make
       every link after the first jump to the wrong place. */
    if (used[base] === undefined) {
      used[base] = 0;
      return base;
    }
    used[base] += 1;
    return base + "-" + used[base];
  }

  var links = [];

  headings.forEach(function (h) {
    if (!h.id) h.id = slug(h.textContent || "");

    var li = document.createElement("li");
    var a = document.createElement("a");
    a.href = "#" + h.id;
    a.textContent = (h.textContent || "").trim();
    li.appendChild(a);
    list.appendChild(li);
    links.push({ a: a, h: h });
  });

  nav.hidden = false;

  /* Smooth scrolling only where the reader has not asked for less motion. */
  var reduce =
    window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  if (!reduce) document.documentElement.style.scrollBehavior = "smooth";

  /*
   * Highlight the section currently being read.
   *
   * Computed from scroll position rather than IntersectionObserver: the
   * observer fires repeatedly *during* a smooth scroll and whichever callback
   * happens to land last wins, which meant clicking a link could leave the
   * neighbouring section highlighted. Reading positions directly is
   * deterministic — the answer is the same whenever it is asked.
   *
   * The active section is the last heading that has passed the reading line,
   * with a bias so the section you just jumped to counts immediately.
   */
  var LINE = 120;
  var current = null;

  /* A clicked link pins itself until the reader scrolls away.
   *
   * Near the end of a document the page runs out of room to scroll, so a
   * heading jumped to cannot reach the reading line and the computed answer
   * would name a different section than the one just asked for. An explicit
   * click is a clearer statement of intent than any position heuristic, so it
   * wins until the reader moves on. */
  var pinned = null;
  /* null means "the jump is still in flight". It must not default to 0: the
     smooth scroll fires scroll events immediately, and comparing the live
     position against 0 unpins on the first one, before the settle position has
     been recorded. */
  var pinnedY = null;

  function apply(active) {
    if (active === current) return;
    links.forEach(function (l) { l.a.classList.toggle("is-current", l === active); });
    current = active;
  }

  function update() {
    /* In flight, or still parked where the jump landed — keep the section that
       was asked for. */
    if (pinned && (pinnedY === null || Math.abs(window.scrollY - pinnedY) < 80)) {
      apply(pinned);
      return;
    }
    pinned = null;

    var active = links[0];
    for (var i = 0; i < links.length; i++) {
      if (links[i].h.getBoundingClientRect().top <= LINE) active = links[i];
      else break;
    }
    apply(active);
  }

  links.forEach(function (l) {
    l.a.addEventListener("click", function () {
      pinned = l;
      apply(l);
      /* Record where the smooth scroll settles, so later movement can be told
         apart from the jump itself. */
      pinnedY = null;
      setTimeout(function () {
        if (pinned === l) pinnedY = window.scrollY;
      }, 700);
    });
  });

  var ticking = false;
  function onScroll() {
    if (ticking) return;
    ticking = true;
    requestAnimationFrame(function () {
      update();
      ticking = false;
    });
  }

  window.addEventListener("scroll", onScroll, { passive: true });
  window.addEventListener("resize", onScroll, { passive: true });
  update();
})();
